import { CommentOutlined } from '@ant-design/icons';
import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * ปุ่มลอยเปิดคู่คิด — มุมขวาล่าง ลากย้ายที่ได้
 *
 * ────────────────────────────────────────────────────────────────
 *  ทำไมต้องลากได้
 * ────────────────────────────────────────────────────────────────
 *
 * ปุ่มลอยทับเนื้อหาที่อยู่ข้างหลังเสมอ ในผังองค์กรที่กางออกได้ทุกทิศ
 * มุมขวาล่างอาจเป็นที่ที่การ์ดสำคัญอยู่พอดีสำหรับผู้ใช้บางคน
 * ปุ่มที่ย้ายไม่ได้จะกลายเป็นสิ่งที่ต้องทนไปตลอด
 *
 * ────────────────────────────────────────────────────────────────
 *  ⚠ สามอย่างที่ปุ่มลากได้ต้องทำให้ถูก ไม่งั้นพังแบบน่ารำคาญ
 * ────────────────────────────────────────────────────────────────
 *
 * 1. **แยกลากออกจากกด** — ถ้าถือว่าปล่อยเมาส์ = กด ผู้ใช้ที่ลากปุ่มเสร็จจะโดนเปิด
 *    หน้าต่างทุกครั้ง ที่นี่ใช้ระยะขยับเกิน DRAG_SLOP ถึงจะนับว่าลาก
 *
 * 2. **หนีบให้อยู่ในจอเสมอ** — ทั้งตอนลากและตอนหน้าต่างถูกย่อ
 *    ถ้าจำตำแหน่งไว้ตอนจอกว้าง แล้วเปิดใหม่บนจอแคบ ปุ่มจะไปอยู่นอกจอและกดไม่ได้อีกเลย
 *    ซึ่งแปลว่าเข้าคู่คิดไม่ได้ถาวรจนกว่าจะล้าง localStorage
 *
 * 3. **setPointerCapture** — ไม่งั้นลากเร็ว ๆ แล้วเมาส์หลุดออกนอกปุ่ม
 *    เหตุการณ์จะขาด ปุ่มค้างกลางทาง
 */

const POS_KEY = 'yana.kookit.pos';

/** ระยะที่ถือว่า "ลาก" ไม่ใช่ "กด" — นิ้วบนจอสัมผัสขยับเล็กน้อยเสมอแม้ตั้งใจกด */
const DRAG_SLOP = 5;

/** ระยะห่างขั้นต่ำจากขอบจอ */
const EDGE = 10;

const SIZE = 56;

interface Pos {
  /** ระยะจากขอบขวา และขอบล่าง — ยึดมุมขวาล่างเพราะเป็นตำแหน่งตั้งต้น */
  right: number;
  bottom: number;
}

const DEFAULT_POS: Pos = { right: 20, bottom: 20 };

function clamp(p: Pos): Pos {
  const maxRight = Math.max(window.innerWidth - SIZE - EDGE, EDGE);
  const maxBottom = Math.max(window.innerHeight - SIZE - EDGE, EDGE);
  return {
    right: Math.min(Math.max(p.right, EDGE), maxRight),
    bottom: Math.min(Math.max(p.bottom, EDGE), maxBottom),
  };
}

function readPos(): Pos {
  try {
    const raw = localStorage.getItem(POS_KEY);
    if (!raw) return DEFAULT_POS;
    const v = JSON.parse(raw) as Partial<Pos>;
    if (typeof v.right !== 'number' || typeof v.bottom !== 'number') return DEFAULT_POS;
    return clamp({ right: v.right, bottom: v.bottom });
  } catch {
    return DEFAULT_POS;
  }
}

export function KookitLauncher({ onOpen, hidden }: { onOpen: () => void; hidden: boolean }) {
  const [pos, setPos] = useState<Pos>(readPos);
  const [dragging, setDragging] = useState(false);
  const start = useRef<{ x: number; y: number; pos: Pos; moved: boolean } | null>(null);

  // จอถูกย่อ ปุ่มอาจหลุดออกนอกขอบ — หนีบกลับเข้ามาเสมอ
  useEffect(() => {
    const onResize = () => setPos((p) => clamp(p));
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const onPointerDown = useCallback(
    (e: React.PointerEvent<HTMLButtonElement>) => {
      e.currentTarget.setPointerCapture(e.pointerId);
      start.current = { x: e.clientX, y: e.clientY, pos, moved: false };
      setDragging(true);
    },
    [pos],
  );

  const onPointerMove = useCallback((e: React.PointerEvent<HTMLButtonElement>) => {
    const s = start.current;
    if (!s) return;
    const dx = e.clientX - s.x;
    const dy = e.clientY - s.y;
    if (!s.moved && Math.hypot(dx, dy) < DRAG_SLOP) return;
    s.moved = true;
    // ลากขวา = right ลดลง · ลากลง = bottom ลดลง เพราะยึดจากมุมขวาล่าง
    setPos(clamp({ right: s.pos.right - dx, bottom: s.pos.bottom - dy }));
  }, []);

  const onPointerUp = useCallback(
    (e: React.PointerEvent<HTMLButtonElement>) => {
      const s = start.current;
      start.current = null;
      setDragging(false);
      if (!s) return;
      if (!s.moved) {
        onOpen();
        return;
      }
      e.currentTarget.releasePointerCapture?.(e.pointerId);
      setPos((p) => {
        try {
          localStorage.setItem(POS_KEY, JSON.stringify(p));
        } catch {
          /* จำตำแหน่งข้ามรอบไม่ได้ก็ยังใช้งานได้ */
        }
        return p;
      });
    },
    [onOpen],
  );

  if (hidden) return null;

  return (
    <button
      type="button"
      className={`kkfab${dragging ? ' is-drag' : ''}`}
      style={{ right: pos.right, bottom: pos.bottom }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={() => {
        start.current = null;
        setDragging(false);
      }}
      /* ⚠ ต้องมี onKeyDown แยก — pointer event ไม่ยิงเมื่อกดด้วยคีย์บอร์ด
         และ onClick ใช้ไม่ได้เพราะจะยิงซ้ำกับ onPointerUp ตอนกดด้วยเมาส์ */
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen();
        }
      }}
      aria-label="เปิดคู่คิด — ถาม-ตอบเรื่องอัตรากำลังด้วย AI (ลากเพื่อย้ายตำแหน่งปุ่มได้)"
      title="คู่คิด"
    >
      <CommentOutlined aria-hidden="true" />
    </button>
  );
}
